/**
 * Estado de la organización del usuario en el shell (OT-0073 §4.4).
 *
 *   - `OrganizationNotice`: fila del layout entre el Topbar y el contenido.
 *     En solo lectura (beta vencida), banner para todos; si a la beta le
 *     quedan `TRIAL_ENDING_NOTICE_DAYS` días o menos, aviso para los admins
 *     con el día en que pasa a solo lectura. Si no aplica, no ocupa espacio.
 *   - `SuspendedView`: pantalla que sustituye a la app cuando la organización
 *     está suspendida; ninguna ruta del tenant se monta.
 *
 * Los tres muestran el contacto de soporte de HIVA (`tenant.support_email`).
 *
 * La fuente es `/me` (`tenant.access`, `tenant.trial_ends_at`); el acceso lo
 * calcula el backend.
 */

import { AlertTriangle, Clock, Lock } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'

import type { MeResponse } from '../../api/me'
import { useSignOut } from '../../auth/useSignOut'
import {
  TRIAL_ENDING_NOTICE_DAYS,
  daysUntilReadOnly,
  formatDay,
} from '../../lib/organizationState'
import { isAdmin } from '../../lib/tiers'

export function OrganizationNotice({ me }: { me: MeResponse | undefined }) {
  const { t, i18n } = useTranslation('shell')
  const tenant = me?.tenant
  if (!tenant) return null

  if (tenant.access === 'read_only') {
    return (
      <div
        role="status"
        className="flex shrink-0 items-start gap-2 border-b border-review/30 bg-review-soft px-6 py-2.5 text-sm text-ink"
      >
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-review" aria-hidden />
        <p>
          {t('organization.read_only_banner')} <SupportContact email={tenant.support_email} />
        </p>
      </div>
    )
  }

  const days = tenant.status === 'trial' ? daysUntilReadOnly(tenant.trial_ends_at) : null
  if (
    tenant.access === 'full' &&
    days !== null &&
    days >= 0 &&
    days <= TRIAL_ENDING_NOTICE_DAYS &&
    isAdmin(me?.role)
  ) {
    return (
      <div
        role="status"
        className="flex shrink-0 items-start gap-2 border-b border-line bg-brand-soft px-6 py-2 text-sm text-ink"
      >
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
        <p>
          {t('organization.trial_ending_notice', {
            count: days,
            date: formatDay(tenant.trial_ends_at, i18n.language),
          })}{' '}
          <SupportContact email={tenant.support_email} />
        </p>
      </div>
    )
  }
  return null
}

export function SuspendedView({
  organizationName,
  supportEmail,
}: {
  organizationName: string
  supportEmail: string | null
}) {
  const { t } = useTranslation('shell')
  const signOut = useSignOut()
  return (
    <section className="w-full max-w-md rounded-md border border-line bg-surface p-8">
      <p className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide text-critical">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        {t('organization.suspended.code')}
      </p>
      <h1 className="mt-3 font-display text-2xl text-ink">
        {t('organization.suspended.title')}
      </h1>
      <p className="mt-2 font-body text-sm text-ink-muted">
        {t('organization.suspended.body', { name: organizationName })}{' '}
        <SupportContact email={supportEmail} />
      </p>
      <button
        type="button"
        onClick={signOut}
        className="mt-6 inline-flex items-center justify-center rounded-md bg-brand px-5 py-2.5 font-ui text-sm text-canvas transition-colors hover:bg-brand-deep focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        {t('organization.suspended.sign_out')}
      </button>
    </section>
  )
}

/** "Escríbenos a soporte@…" con enlace; nada si el ambiente no configura el
 *  contacto (`Settings.support_email`). */
function SupportContact({ email }: { email: string | null }) {
  const { t } = useTranslation('shell')
  if (!email) return null
  return (
    <Trans
      i18nKey="organization.contact"
      t={t}
      values={{ email }}
      components={{
        // No `link`: el parser de Trans lo toma como la etiqueta HTML vacía.
        mail: <a href={`mailto:${email}`} className="font-medium text-brand hover:underline" />,
      }}
    />
  )
}
